import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Search, Users, Trash2 } from "lucide-react";

interface LinkedAccount {
  userId: string;
  fullName: string;
}

interface AccountLinkingProps {
  currentUserId: string;
  linkedAccounts?: LinkedAccount[];
}

export const AccountLinking = ({ currentUserId, linkedAccounts = [] }: AccountLinkingProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<{ email: string; full_name: string; userId: string }[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { toast } = useToast();

  const searchAccounts = async () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    setHasSearched(true);
    try {
      // Search for approved group leaders by email
      const { data, error } = await supabase
        .from('group_leader_requests')
        .select('email, full_name')
        .eq('status', 'approved')
        .ilike('email', `%${searchTerm}%`)
        .limit(10);

      if (error) throw error;

      // Filter out current user
      const { data: currentUserData } = await supabase.auth.getUser();
      const currentUserEmail = currentUserData?.user?.email;
      
      const filteredResults = (data || []).filter(account => 
        account.email !== currentUserEmail
      );

      // Look up UUID for each result
      const resultsWithUuid = await Promise.all(
        filteredResults.map(async (account) => {
          const { data: userIdData } = await supabase.rpc('get_user_id_by_email', {
            email_input: account.email,
          });
          return {
            ...account,
            userId: userIdData || '',
          };
        })
      );

      // Only show results that have a valid UUID
      setSearchResults(resultsWithUuid.filter(r => r.userId));
    } catch (error) {
      console.error('Error searching accounts:', error);
      toast({
        title: "Fel",
        description: "Kunde inte söka efter konton",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const linkAccount = async (targetUserId: string) => {
    try {
      // Create bidirectional link using UUIDs
      const { error } = await supabase
        .from('account_links')
        .insert([
          { user_id: currentUserId, linked_user_id: targetUserId },
          { user_id: targetUserId, linked_user_id: currentUserId }
        ]);

      if (error) throw error;

      toast({
        title: "Klart",
        description: "Kontot har kopplats",
      });
      
      setSearchResults([]);
      setSearchTerm("");
      
      window.location.reload();
    } catch (error) {
      console.error('Error linking account:', error);
      toast({
        title: "Fel",
        description: "Kunde inte koppla kontot",
        variant: "destructive",
      });
    }
  };

  const unlinkAccount = async (targetUserId: string) => {
    try {
      const { error: error1 } = await supabase
        .from('account_links')
        .delete()
        .match({ user_id: currentUserId, linked_user_id: targetUserId });

      const { error: error2 } = await supabase
        .from('account_links')
        .delete()
        .match({ user_id: targetUserId, linked_user_id: currentUserId });

      const error = error1 || error2;
      if (error) throw error;

      toast({
        title: "Klart",
        description: "Kontot har kopplats bort",
      });
      
      window.location.reload();
    } catch (error) {
      console.error('Error unlinking account:', error);
      toast({
        title: "Fel",
        description: "Kunde inte koppla bort kontot",
        variant: "destructive",
      });
    }
  };

  const linkedUserIds = linkedAccounts.map(a => a.userId);

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Koppla kontot till annan gruppledare</h3>
        </div>
        
        <p className="text-sm text-muted-foreground">
          Här kopplar du och din kollega ihop era konton om ni ska hålla grupperna gemensamt. 
          Ni kommer då att se varandras QR-koder.
        </p>

        {linkedAccounts.length > 0 && (
          <div className="space-y-2">
            <Label>Kopplade konton:</Label>
            {linkedAccounts.map((account) => (
              <div key={account.userId} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="font-medium">{account.fullName}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unlinkAccount(account.userId)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="search">Sök efter gruppledare med e-post:</Label>
          <div className="flex gap-2">
            <Input
              id="search"
              placeholder="Skriv e-postadress..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchAccounts()}
            />
            <Button 
              onClick={searchAccounts} 
              disabled={isSearching || !searchTerm.trim()}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {hasSearched && searchResults.length === 0 && !isSearching && (
          <div className="p-4 text-center text-muted-foreground bg-muted/50 rounded-lg">
            Inga gruppledare hittades med den e-postadressen.
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="space-y-2">
            <Label>Sökresultat:</Label>
            {searchResults.map((account) => (
              <div key={account.userId} className="flex items-center justify-between p-2 border rounded">
                <div>
                  <div className="font-medium">{account.full_name}</div>
                  <div className="text-sm text-muted-foreground">{account.email}</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => linkAccount(account.userId)}
                  disabled={linkedUserIds.includes(account.userId)}
                >
                  {linkedUserIds.includes(account.userId) ? 'Redan kopplad' : 'Koppla'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
